import { useAuth0 } from "@auth0/auth0-react";
import React, { useState, useEffect } from "react";
import { Whisper, Tooltip } from "rsuite";
import styled from "styled-components";
import axiosInstance from "../../../../../appHelpers/axiosInstance";
import { DateTime } from "luxon";
import InfoOutlineIcon from "@rsuite/icons/InfoOutline";
import { SeverityAlert } from "../../../../../appHelpers/appCommonSC";
import {
  SectionRibbon,
  SpacedGlassCard,
  Picker,
  NoDataState,
  Loader,
} from "../../../../ui";
import { colors } from "../../../../ui/theme/colors";
import { shadows } from "../../../../ui/theme/shadows";

export const UserAlerts = ({ user }) => {
  const [detectedAlerts, setDetectedAlerts] = useState([]);
  const [displayAlerts, setDisplayAlerts] = useState([]);
  const [uniqueSystems, setUniqueSystems] = useState([]);
  const [checkSystems, setCheckedSystems] = useState([]);
  const [loadingDetectedAlerts, setLoadingDetectedAlerts] = useState(true);
  const { getAccessTokenSilently } = useAuth0();

  const getUserDetectedAlerts = async () => {
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const res = await axiosInstance({
        url: `/alert/detected/${user}`,
        data: ``,
        headers: { authorization: `Bearer ${token}` },
      });

      setDetectedAlerts(res?.data[0]);
      setDisplayAlerts(res?.data[0]);
      setUniqueSystems([
        ...new Set(res?.data[0].map((alert) => alert.system_id)),
      ]);
    } catch (error) {
      console.error(error);
    }
    setLoadingDetectedAlerts(false);
  };

  const filterAlertList = () => {
    if (checkSystems.length > 0) {
      setDisplayAlerts(
        detectedAlerts.filter((alert) =>
          checkSystems.includes(alert.system_id),
        ),
      );
    } else {
      setDisplayAlerts(detectedAlerts);
    }
  };

  useEffect(() => {
    getUserDetectedAlerts();
  }, [user]);

  useEffect(() => {
    filterAlertList();
  }, [checkSystems]);

  useEffect(() => {}, [displayAlerts]);

  return (
    <div>
      <SectionRibbon title="Alert History" />

      {loadingDetectedAlerts ? (
        <LoadingContainer>
          <Loader />
        </LoadingContainer>
      ) : displayAlerts.length === 0 ? (
        <NoDataState
          icon="📧"
          title="No Alert History"
          message="No alerts have been triggered for this user in the past 7 days."
        />
      ) : (
        <>
          <SpacedGlassCard>
            <StatsContainer>
              <StatItem>
                <StatLabel>Unique Systems</StatLabel>
                <StatValue>{uniqueSystems.length}</StatValue>
              </StatItem>

              <FilterContainer>
                <Picker
                  label="Filter by System"
                  value={checkSystems}
                  onChange={setCheckedSystems}
                  data={uniqueSystems.map((item) => ({
                    label: item,
                    value: item,
                  }))}
                  multiple={true}
                  placeholder="All Systems"
                  searchable={true}
                />
              </FilterContainer>

              <StatItem>
                <StatLabel>
                  Triggered Alerts
                  <Whisper
                    followCursor
                    speaker={
                      <Tooltip>Triggered Alerts in the Past 7 Days.</Tooltip>
                    }
                  >
                    <InfoIcon />
                  </Whisper>
                </StatLabel>
                <StatValue>{displayAlerts.length}</StatValue>
              </StatItem>
            </StatsContainer>
          </SpacedGlassCard>

          <SpacedGlassCard>
            <TableContainer>
              <TableHeader>
                <TableHeaderCell width="15%">System ID</TableHeaderCell>
                <TableHeaderCell width="15%">Info</TableHeaderCell>
                <TableHeaderCell width="8%">Severity</TableHeaderCell>
                <TableHeaderCell width="25%">Message</TableHeaderCell>
                <TableHeaderCell width="8%">Email</TableHeaderCell>
                <TableHeaderCell width="8%">SMS</TableHeaderCell>
                <TableHeaderCell width="21%">Sent At</TableHeaderCell>
              </TableHeader>

              {displayAlerts?.map((alert, index) => (
                <TableRow key={alert.customer_id} status={alert?.email_status}>
                  <TableCell width="15%" data-label="System ID">
                    {alert.cus_sys_id ? (
                      <>
                        <ItemMainText>{alert.cus_sys_id}</ItemMainText>
                        <ItemSubText>{alert.system_id}</ItemSubText>
                      </>
                    ) : (
                      alert.system_id
                    )}
                  </TableCell>

                  <TableCell width="15%" data-label="Info">
                    {alert.manufacturer ? (
                      <>
                        <ItemMainText>{alert.manufacturer}</ItemMainText>
                        <ItemSubText>{alert.model}</ItemSubText>
                      </>
                    ) : (
                      <span>—</span>
                    )}
                  </TableCell>

                  <TableCell width="8%" align="center" data-label="Severity">
                    <SeverityAlert severity={alert?.severity}>
                      {alert?.severity.toUpperCase()}
                    </SeverityAlert>
                  </TableCell>

                  <TableCell width="25%" data-label="Message">
                    {alert.operator === "offline" ||
                    alert.operator === "frozen_host" ? (
                      <>
                        {alert.field_name_alias}: {alert.threshold}{" "}
                        {alert.threshold_units}
                      </>
                    ) : alert.operator === "contains" ||
                      alert.operator === "composite_equals" ? (
                      <>{alert.field_name_alias}</>
                    ) : alert.operator === "equals" &&
                      alert.field_name_alias?.toLowerCase() === "compressor" ? (
                      <>{alert.field_name_alias} is OFF!</>
                    ) : (
                      <>
                        {alert.field_name_alias} {alert.resolved_field_units} is{" "}
                        {alert.operator.replace(/_/g, " ")} {alert.threshold}{" "}
                        {alert.threshold_units}
                      </>
                    )}
                  </TableCell>

                  <TableCell width="8%" align="center" data-label="Email">
                    <StatusBadge status={alert.email_status}>
                      {alert.email_status}
                    </StatusBadge>
                  </TableCell>

                  <TableCell width="8%" align="center" data-label="SMS">
                    {alert.sms_status && alert.severity === "medium" ? (
                      <span>—</span>
                    ) : (
                      <StatusBadge status={alert.sms_status}>
                        {alert.sms_status}
                      </StatusBadge>
                    )}
                  </TableCell>

                  <TableCell width="21%" align="center" data-label="Sent At">
                    <TimeStamp>
                      {DateTime.fromISO(alert.inserted_at).toFormat("ff")}
                    </TimeStamp>
                  </TableCell>
                </TableRow>
              ))}
            </TableContainer>
          </SpacedGlassCard>
        </>
      )}
    </div>
  );
};

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 80px 20px;
`;

const StatsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr 1fr;
  gap: 32px;
  align-items: end;

  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 24px;
    text-align: center;
  }
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const StatLabel = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${colors.neutral[600]};
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${colors.primary.main};
`;

const FilterContainer = styled.div`
  width: 100%;
`;

const InfoIcon = styled(InfoOutlineIcon)`
  cursor: pointer;
  color: ${colors.neutral[500]};

  &:hover {
    color: ${colors.primary.main};
  }
`;

const TableContainer = styled.div`
  width: 100%;
  border-radius: 16px;
  overflow: hidden;
  border: 1px solid ${colors.neutral[200]};
`;

const TableHeader = styled.div`
  display: flex;
  background: ${colors.neutral[50]};
  border-bottom: 2px solid ${colors.neutral[200]};
  font-weight: 600;
  font-size: 12px;
  color: ${colors.neutral[700]};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const TableHeaderCell = styled.div`
  padding: 16px 12px;
  width: ${(props) => props.width};
  text-align: ${(props) => props.align || "left"};
  flex-shrink: 0;
`;

const TableRow = styled.div`
  display: flex;
  border-bottom: 1px solid ${colors.neutral[100]};
  transition: background-color 0.2s ease;
  background-color: ${({ status }) =>
    status === "SENT"
      ? "rgba(34, 197, 94, 0.05)"
      : status === "SUPPRESSED"
        ? "rgba(156, 163, 175, 0.05)"
        : status === "DISABLED"
          ? "rgba(156, 163, 175, 0.05)"
          : status === "FAILED"
            ? "rgba(239, 68, 68, 0.05)"
            : "white"};

  &:hover {
    background-color: ${colors.neutral[25]};
  }

  &:last-child {
    border-bottom: none;
  }

  @media screen and (max-width: 768px) {
    flex-direction: column;
    padding: 16px;
    gap: 12px;

    &:not(:last-child) {
      border-bottom: 1px solid ${colors.neutral[200]};
    }
  }
`;

const TableCell = styled.div`
  padding: 16px 12px;
  width: ${(props) => props.width};
  text-align: ${(props) => props.align || "left"};
  flex-shrink: 0;
  display: flex;
  align-items: center;
  font-size: 14px;

  @media screen and (max-width: 768px) {
    width: 100% !important;
    padding: 4px 0;
    text-align: left !important;

    &::before {
      content: attr(data-label);
      font-weight: 600;
      color: ${colors.neutral[600]};
      min-width: 120px;
      margin-right: 16px;
    }
  }
`;

const StatusBadge = styled.span`
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;

  ${({ status }) => {
    switch (status) {
      case "SENT":
        return `
          background: rgba(34, 197, 94, 0.1);
          color: #16a34a;
          border: 1px solid rgba(34, 197, 94, 0.2);
        `;
      case "FAILED":
        return `
          background: rgba(239, 68, 68, 0.1);
          color: #dc2626;
          border: 1px solid rgba(239, 68, 68, 0.2);
        `;
      case "SUPPRESSED":
      case "DISABLED":
        return `
          background: rgba(156, 163, 175, 0.1);
          color: #6b7280;
          border: 1px solid rgba(156, 163, 175, 0.2);
        `;
      default:
        return `
          background: ${colors.neutral[100]};
          color: ${colors.neutral[600]};
          border: 1px solid ${colors.neutral[200]};
        `;
    }
  }}
`;

const TimeStamp = styled.span`
  font-family: "SF Mono", Monaco, monospace;
  font-size: 13px;
  color: ${colors.neutral[600]};
`;

const ItemMainText = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${colors.neutral[900]};
  margin-bottom: 2px;
`;

const ItemSubText = styled.div`
  font-size: 12px;
  color: ${colors.neutral[500]};
  font-weight: normal;
`;
